/**
 * Client-safe API utilities
 * This file can be imported by both client and server components
 */

import { API_COLLECTIONS_BASE, API_BASE_URL, API_CANONICAL_BASE_URL } from './constants';

/**
 * Check if an endpoint is a custom API (non-Quintype)
 * Custom APIs should NOT be normalized - they keep their original URL
 */
function isCustomApiEndpoint(endpoint: string): boolean {
  // Custom API patterns that should NOT be normalized
  const customPatterns = [
    '/api/authors/',
    '/api/public/',
    'api.dinamani.mo.vc/api/authors',
    'api.dinamani.mo.vc/api/public',
    'builderapi.dinamani.com/api/',
    'builderapi.dinamani.com/api/authors',
    'builderapi.dinamani.com/api/public',
  ];

  for (const pattern of customPatterns) {
    if (endpoint.includes(pattern)) {
      return true;
    }
  }

  // If it's a full URL that doesn't contain /api/v1/ or /collections/, it's likely custom
  if ((endpoint.startsWith('http://') || endpoint.startsWith('https://')) &&
      !endpoint.includes('/api/v1/') &&
      !endpoint.includes('/collections/') &&
      !endpoint.includes('quintype.io')) {
    return true;
  }

  return false;
}

/**
 * Normalizes API endpoints from builder JSON to use the centralized API base URL.
 * Handles various URL formats:
 * - Full URLs (https://www.dinamani.com/api/v1/..., https://dinamani.quintype.io/api/v1/...)
 * - Relative paths (/api/v1/..., /collections/...)
 * - Collection slugs (just the slug name)
 *
 * Custom API endpoints (non-Quintype) are returned as-is without normalization.
 */
export function normalizeApiEndpoint(endpoint: string): string {
  if (!endpoint) return endpoint;

  // Custom APIs should NOT be normalized - keep original URL
  if (isCustomApiEndpoint(endpoint)) {
    return endpoint;
  }

  // List of known Quintype API domains to normalize
  const knownDomains = [
    'https://www.dinamani.com/api/v1',
    'https://dinamani.com/api/v1',
    'http://www.dinamani.com/api/v1',
    'http://dinamani.com/api/v1',
    'https://dinamani.quintype.io/api/v1',
    'http://dinamani.quintype.io/api/v1',
    'https://api.dinamani.mo.vc/api/v1',
    'http://api.dinamani.mo.vc/api/v1',
    'https://builderapi.dinamani.com/api/v1',
    'http://builderapi.dinamani.com/api/v1',
    'http://dinamani.internal.quintype.io/api/v1',
    'https://dinamani.internal.quintype.io/api/v1',
  ];

  // Check if endpoint starts with any known domain and normalize it
  for (const domain of knownDomains) {
    if (endpoint.startsWith(domain)) {
      // Extract the path after /api/v1 and prepend the correct base URL
      const path = endpoint.substring(domain.length);
      return `${API_CANONICAL_BASE_URL}${path}`;
    }
  }

  // If it's a relative path starting with /api/v1, normalize it
  if (endpoint.startsWith('/api/v1/')) {
    return `${API_CANONICAL_BASE_URL}${endpoint.substring(7)}`; // Remove /api/v1 prefix
  }

  // If it's a relative path starting with /collections/, use collections base
  if (endpoint.startsWith('/collections/')) {
    return `${API_CANONICAL_BASE_URL}${endpoint}`;
  }

  // If it starts with /, assume it's a path relative to API base
  if (endpoint.startsWith('/')) {
    return `${API_CANONICAL_BASE_URL}${endpoint}`;
  }

  // If it's already a full URL to a different domain, keep it as is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  // Otherwise, assume it's a collection slug
  return `${API_CANONICAL_BASE_URL}/collections/${endpoint}`;
}

/**
 * Convert a canonical API URL to the actual fetch URL.
 * On BK deployments, API_BASE_URL points to the internal service URL for performance.
 * This converts canonical (public) URLs back to the internal URL for HTTP fetching.
 * For local development where API_BASE_URL matches API_CANONICAL_BASE_URL, this is a no-op.
 */
export function toFetchUrl(canonicalUrl: string): string {
  if (canonicalUrl.startsWith(API_CANONICAL_BASE_URL)) {
    return API_BASE_URL + canonicalUrl.substring(API_CANONICAL_BASE_URL.length);
  }
  return canonicalUrl;
}
